import apiClient from '../client'

export interface AgentPersona {
  agent_name: string
  agent_email: string
  agent_gender: string
  agent_title: string
  agent_company: string
  agent_company_pronunciation: string
}

export interface AgentPersonaVersion {
  version: number
  persona: AgentPersona
  created_at: string
  created_by?: string
  is_active: boolean
}

export interface AgentPersonaResponse {
  current: AgentPersona
  current_version: number
  versions: AgentPersonaVersion[]
}

export interface UpdatePersonaResponse {
  success: boolean
  message: string
  version: number
  persona: AgentPersona
}

export interface TestEmailRequest {
  to_email: string
  subject?: string
  agent_id?: number
}

export interface TestEmailResponse {
  success: boolean
  message: string
  from_email: string
  from_name: string
  to_email: string
  subject: string
  language?: string
}

export interface BYOTConfig {
  encrypted_key?: string
  outbound_caller_id?: string
}

export interface BYOTConfigResponse {
  is_configured: boolean
  outbound_caller_id?: string
  encrypted_key_masked?: string
}

export interface BYOTUpdateResponse {
  success: boolean
  message: string
  is_configured: boolean
  outbound_caller_id?: string
  encrypted_key_masked?: string
}

export const engagementApi = {
  /**
   * Get current agent persona and version history
   */
  getPersona: () =>
    apiClient.get<AgentPersonaResponse>('/api/engagement/persona'),

  /**
   * Update agent persona (creates new version)
   */
  updatePersona: (persona: AgentPersona, updated_by?: string) =>
    apiClient.put<UpdatePersonaResponse>('/api/engagement/persona', {
      persona,
      updated_by,
    }),

  /**
   * Rollback to a previous version
   */
  rollbackPersona: (version: number, updated_by?: string) =>
    apiClient.post<UpdatePersonaResponse>(
      `/api/engagement/persona/rollback/${version}`,
      null,
      { params: { updated_by } }
    ),

  /**
   * Delete a persona version
   */
  deletePersonaVersion: (version: number) =>
    apiClient.delete<{ success: boolean; message: string }>(
      `/api/engagement/persona/version/${version}`
    ),

  /**
   * Send a test email to verify email configuration
   */
  sendTestEmail: (request: TestEmailRequest) =>
    apiClient.post<TestEmailResponse>('/api/engagement/test-email', request),

  /**
   * Get BYOT (Bring Your Own Twilio) configuration
   */
  getBYOTConfig: () =>
    apiClient.get<BYOTConfigResponse>('/api/engagement/byot'),

  /**
   * Update BYOT configuration
   */
  updateBYOTConfig: (config: BYOTConfig) =>
    apiClient.put<BYOTUpdateResponse>('/api/engagement/byot', config),

  /**
   * Get real-time engagement batch queue status
   */
  getBatchQueue: (limit: number = 50, hours: number = 1) =>
    apiClient.get<BatchQueueResponse>('/api/engagement/batch/queue', {
      params: { limit, hours }
    }),

  /**
   * Get engagement activity stream
   */
  getActivityStream: (minutes: number = 10) =>
    apiClient.get<ActivityStreamResponse>('/api/engagement/batch/activity-stream', {
      params: { minutes }
    }),
}

// Batch Queue Types
export interface BatchQueueResponse {
  timestamp: string
  stats: {
    today: {
      total_calls: number
      completed: number
      human_answered: number
      voicemail: number
      failed: number
    }
    last_hour: {
      calls_made: number
      leads_engaged: number
    }
    leads_by_source?: Record<string, number>
    leads_by_source_week?: Record<string, number>
  }
  rate_limiter: {
    name?: string
    tokens?: number
    calls_per_second?: number
    recent_calls_count?: number
    error?: string
  }
  circuit_breaker: {
    name?: string
    state?: string
    failure_count?: number
    retry_after?: number
    error?: string
  }
  recent_calls: RecentCall[]
  recent_leads: RecentLead[]
}

export interface RecentCall {
  id: number
  call_id: string
  summary_id: number | null
  lead_id: number
  phone: string | null
  company_name: string | null
  status: string
  answered_by: string | null
  transferred: boolean
  transfer_successful: boolean | null
  duration_seconds: number | null
  turn_count: number | null
  sentiment: string | null
  transcript_preview: string | null
  created_at: string
  completed_at: string | null
}

export interface RecentLead {
  id: number
  company_name: string
  phone: string
  status: string
  call_attempt_count: number
  last_contact_date: string | null
  engagement_status: string | null
  turn_count: number | null
  sentiment: string | null
  last_call_duration: number | null
}

export interface ActivityStreamResponse {
  timestamp: string
  period_minutes: number
  total_events: number
  by_type: Record<string, number>
  stream: ActivityEvent[]
}

export interface ActivityEvent {
  timestamp: string
  type: string
  lead_id: number
  channel: string | null
  subject: string | null
  details: Record<string, unknown>
}
